import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Phone, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { ctaContent, businessInfo } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const CTASection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Content animation
      gsap.fromTo(
        '.cta-content',
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-[600px] flex items-center overflow-hidden">
      {/* Full Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_d0850d40-f350-4ecf-87ac-4e2d9723d534/artifacts/cifzsj5n_ChatGPT%20Image%20Feb%2012%2C%202026%2C%2010_25_37%20AM.png"
          alt="Professional CCTV security camera installation Bayside Melbourne"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        {/* Additional gradient for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
      </div>

      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(229,33,51,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(229,33,51,0.2)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="max-w-3xl">
          {/* Glow Line */}
          <div className="cta-content w-24 h-1 bg-gradient-to-r from-securelec-red to-securelec-red/50 mb-8 rounded-full" style={{ boxShadow: '0 0 20px rgba(229, 33, 51, 0.6)' }} />

          {/* Headline */}
          <h2 className="cta-content text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
            {ctaContent.headline}
          </h2>

          {/* Description */}
          <p className="cta-content text-lg md:text-xl text-white/80 mb-8 leading-relaxed max-w-2xl drop-shadow-md">
            {ctaContent.description}
          </p>

          {/* Trust Features */}
          <div className="cta-content flex flex-wrap gap-6 mb-10">
            {ctaContent.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-securelec-red" />
                <span className="text-sm font-medium text-white/90">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="cta-content flex flex-col sm:flex-row gap-4 mb-10">
            <a href="/get-a-quote">
              <Button
                className="bg-securelec-red hover:bg-securelec-red/90 text-white px-10 py-6 text-lg rounded-md font-bold relative overflow-hidden group transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-securelec-red/30"
                data-testid="cta-get-a-quote-btn"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {ctaContent.ctaPrimary}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </a>
            <a
              href={`tel:${businessInfo.phone.mick}`}
              className="flex items-center justify-center gap-3 border-2 border-white/40 hover:border-securelec-red bg-white/10 backdrop-blur-sm hover:bg-securelec-red/20 text-white px-8 py-4 rounded-md font-semibold transition-all duration-300"
            >
              <Phone className="w-5 h-5" />
              {ctaContent.ctaSecondary}
            </a>
          </div>

          {/* Contact Info */}
          <div className="cta-content flex flex-col sm:flex-row gap-6 pt-8 border-t border-white/20">
            <div>
              <span className="text-white/50 text-sm block">Call Mick</span>
              <a href={`tel:${businessInfo.phone.mick}`} className="text-white hover:text-securelec-red transition-colors font-semibold text-lg">
                {businessInfo.phone.mick}
              </a>
            </div>
            <div className="hidden sm:block w-px h-12 bg-white/20" />
            <div>
              <span className="text-white/50 text-sm block">Call Tom</span>
              <a href={`tel:${businessInfo.phone.tom}`} className="text-white hover:text-securelec-red transition-colors font-semibold text-lg">
                {businessInfo.phone.tom}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Corner Elements */}
      <div className="absolute top-10 right-10 w-20 h-20 border-r-2 border-t-2 border-securelec-red/30 hidden lg:block" />
      <div className="absolute bottom-10 right-10 w-32 h-32 border-r-2 border-b-2 border-white/10 hidden lg:block" />
    </section>
  );
};

export default CTASection;
